import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { ImageProcessorService } from '../image-processor.service';
import { AuthService } from '../auth.service';
import { ProyectosService } from '../proyectos.service';
import { firstValueFrom } from 'rxjs';
import * as JSZip from 'jszip';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {
  title = 'portal-ia';
  isDarkMode = true;
  isAuthenticated = false;
  private themeObserver?: MutationObserver;
  
  // Valores de los controles
  width = 800;
  height = 600;
  backgroundColor = '#FFFFFF';
  mode = 'adaptar';
  imageStyle: 'none' | 'frosted' | 'matte' | 'pastel' | 'sunset' = 'none';
  selectedTemplate: string | null = null;
  showTemplateDropdown = false;
  
  // Plantillas predefinidas
  templates = [
    // Nuevas plantillas de ratios (del script.js)
    { id: '1-1-post', name: '1:1 (Post Cuadrado)', description: 'Post Cuadrado', width: 1080, height: 1080, ratio: '1:1', ratioClass: 'ratio-1-1', image: null },
    { id: '4-5', name: '4:5 (Post Vertical)', description: 'Post Vertical', width: 1080, height: 1350, ratio: '4:5', ratioClass: 'ratio-4-5', image: null },
    { id: '16-9', name: '16:9 (Fotografía horizontal)', description: 'Fotografía horizontal', width: 1920, height: 1080, ratio: '16:9', ratioClass: 'ratio-16-9', image: null },
    { id: '9-16', name: '9:16 (Vertical historias o reels)', description: 'Vertical historias o reels', width: 1080, height: 1920, ratio: '9:16', ratioClass: 'ratio-9-16', image: null },
    { id: '1-1-profile', name: '1:1 (Foto de Perfil)', description: 'Foto de Perfil', width: 800, height: 800, ratio: '1:1', ratioClass: 'ratio-1-1', image: null },
    // Plantillas existentes (ajustadas al nuevo formato)
    { id: 'pixel2', name: 'Google Pixel/Pixel 2', description: 'Google Pixel/Pixel 2', width: 1080, height: 1920, ratio: null, ratioClass: null, image: 'Google PixelPixel 2.png' },
    { id: 'pixel2xl', name: 'Google Pixel XL/Pixel 2 XL', description: 'Google Pixel XL/Pixel 2 XL', width: 1440, height: 2560, ratio: null, ratioClass: null, image: 'GooglePixelXLPixel2XL.png' },
    { id: 'iphone-icon', name: 'iOS iPhone App Icon', description: 'iOS iPhone App Icon', width: 120, height: 120, ratio: '1:1', ratioClass: 'ratio-1-1', image: 'iOS iPhone App Icon.png' },
    { id: 'ipad-pro-icon', name: 'iOS iPad Pro App Icon', description: 'iOS iPad Pro App Icon', width: 167, height: 167, ratio: '1:1', ratioClass: 'ratio-1-1', image: 'iOS iPad Pro App Icon.png' },
    { id: 'ipad-icon', name: 'iOS iPad App Icon', description: 'iOS iPad App Icon', width: 152, height: 152, ratio: '1:1', ratioClass: 'ratio-1-1', image: 'iOS iPad App Icon.png' },
    { id: 'macos-512', name: 'MacOS 512 App Icon', description: 'MacOS 512 App Icon', width: 512, height: 512, ratio: '1:1', ratioClass: 'ratio-1-1', image: 'MacOS 512 App Icon.png' },
    { id: 'macos-256', name: 'MacOS 256 App Icon', description: 'MacOS 256 App Icon', width: 256, height: 256, ratio: '1:1', ratioClass: 'ratio-1-1', image: 'MacOS 256 App Icon.png' },
    { id: 'macos-128', name: 'MacOS 128 App Icon', description: 'MacOS 128 App Icon', width: 128, height: 128, ratio: '1:1', ratioClass: 'ratio-1-1', image: 'MacOS 128 App Icon.png' },
    { id: 'apple-icono-1024', name: 'Apple 1024 App Icon', description: 'Apple 1024 App Icon', width: 1024, height: 1024, ratio: null, ratioClass: null, image: 'MacOS 128 App Icon.png' }
    
  ];
  
  // Archivo seleccionado (puede ser ZIP o imágenes individuales)
  selectedFile: File | null = null;
  selectedFiles: File[] = [];
  // URLs de miniaturas para las imágenes cargadas
  imagePreviewUrls: string[] = [];
  // URL de la vista previa procesada
  processedPreviewUrl: string | null = null;
  isGeneratingPreview = false;
  
  // Estados de procesamiento
  isProcessing = false;
  processingProgress = 0;
  errorMessage = '';
  successMessage = '';
  private progressInterval: any = null;
  
  // Mensaje del chat con efecto de escritura
  chatMessage = '';
  isTyping = true;
  private fullMessage = 'Hola! Soy tu asistente de procesamiento de imágenes. Puedo ayudarte a redimensionar, cambiar el fondo y procesar múltiples imágenes en distintos formatos. ¿Qué necesitas hacer hoy?';
  
  constructor(
    private imageProcessorService: ImageProcessorService,
    private authService: AuthService,
    private router: Router,
    private proyectosService: ProyectosService
  ) {}
  
  ngOnInit() {
    // Verificar estado de autenticación
    this.isAuthenticated = this.authService.isAuthenticated();
    
    // Detectar tema actual del body
    this.detectTheme();
    // Observar cambios en el tema
    this.observeThemeChanges();
    
    // Iniciar efecto de escritura siempre
    this.startTypingEffect();
  }
  
  goToLogin() {
    this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    // Limpiar el observer cuando el componente se destruye
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    // Limpiar miniaturas de imágenes
    this.cleanupImagePreviews();
  }

  private detectTheme() {
    this.isDarkMode = document.body.classList.contains('dark-theme');
  }

  private observeThemeChanges() {
    // Observar cambios en las clases del body
    this.themeObserver = new MutationObserver(() => {
      this.detectTheme();
    });
    
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
  
  private startTypingEffect() {
    let index = 0;
    const typingSpeed = 30; // milisegundos entre cada letra
    
    const typeChar = () => {
      if (index < this.fullMessage.length) {
        this.chatMessage = this.fullMessage.substring(0, index + 1);
        index++;
        setTimeout(typeChar, typingSpeed);
      } else {
        this.isTyping = false;
      }
    };
    
    // Pequeño delay antes de empezar a escribir
    setTimeout(() => {
      typeChar();
    }, 500);
  }
  
  
  // Extensiones válidas
  private readonly validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.zip'];
  
  private isValidFile(file: File): boolean {
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return this.validExtensions.includes(extension);
  }
  
  onFileSelected(event: any) {
    const files = Array.from(event.target.files) as File[];
    if (files.length === 0) return;
    
    // Validar extensiones
    const invalidFiles = files.filter(f => !this.isValidFile(f));
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map(f => f.name).join(', ');
      this.showError(`El formato que intentó subir no es aceptado. Archivos rechazados: ${invalidNames}. Solo se aceptan: JPG, PNG, WebP o ZIP.`);
      return;
    }
    
    // Si hay un ZIP, solo permitir un archivo
    const zipFile = files.find(f => f.name.toLowerCase().endsWith('.zip'));
    if (zipFile && files.length > 1) {
      this.showError('Si selecciona un archivo ZIP, no puede seleccionar otros archivos. Seleccione solo el ZIP o solo imágenes individuales.');
      return;
    }
    
    // Validar autenticación para archivos ZIP
    if (zipFile) {
      // Verificar si el usuario está autenticado
      this.isAuthenticated = this.authService.isAuthenticated();
      if (!this.isAuthenticated) {
        this.showError('Debes iniciar sesión para poder cargar archivos ZIP. Por favor, inicia sesión para continuar.');
        // Limpiar el input de archivo
        event.target.value = '';
        return;
      }
      // Solo un ZIP
      this.selectedFile = zipFile;
      this.selectedFiles = [];
      // Limpiar miniaturas si había imágenes
      this.cleanupImagePreviews();
    } else {
      // Múltiples imágenes (no requieren autenticación)
      this.selectedFile = null;
      this.selectedFiles = files;
      // Generar miniaturas para las imágenes
      this.generateImagePreviews(files);
    }
  }
  
  onFileDropped(event: DragEvent) {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length === 0) return;
    
    // Validar extensiones
    const invalidFiles = files.filter(f => !this.isValidFile(f));
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map(f => f.name).join(', ');
      this.showError(`El formato que intentó subir no es aceptado. Archivos rechazados: ${invalidNames}. Solo se aceptan: JPG, PNG, WebP o ZIP.`);
      return;
    }
    
    // Si hay un ZIP, solo permitir un archivo
    const zipFile = files.find(f => f.name.toLowerCase().endsWith('.zip'));
    if (zipFile && files.length > 1) {
      this.showError('Si arrastra un archivo ZIP, no puede arrastrar otros archivos. Arrastre solo el ZIP o solo imágenes individuales.');
      return;
    }
    
    // Validar autenticación para archivos ZIP
    if (zipFile) {
      // Verificar si el usuario está autenticado
      this.isAuthenticated = this.authService.isAuthenticated();
      if (!this.isAuthenticated) {
        this.showError('Debes iniciar sesión para poder cargar archivos ZIP. Por favor, inicia sesión para continuar.');
        return;
      }
      // Solo un ZIP
      this.selectedFile = zipFile;
      this.selectedFiles = [];
      // Limpiar miniaturas si había imágenes
      this.cleanupImagePreviews();
    } else {
      // Múltiples imágenes individuales (NO se suben automáticamente)
      this.selectedFile = null;
      this.selectedFiles = files;
      // Generar miniaturas para las imágenes
      this.generateImagePreviews(files);
    }
  }
  
  // Subir imágenes procesadas al storage y guardar en BD
  async uploadProcessedImages(processedImages: File[]) {
    const userId = this.authService.getUserId();
    
    // Si no hay usuario autenticado, no subir (las imágenes se pueden procesar pero no guardar)
    if (!userId) {
      console.log('Usuario no autenticado, las imágenes procesadas no se guardarán en BD');
      return;
    }
    
    let uploadedCount = 0;
    
    // Subir cada imagen procesada individualmente
    for (const file of processedImages) {
      if (file.type.startsWith('image/')) {
        try {
          // Subir al storage usando el endpoint del procesador (con nombre original)
          const response = await firstValueFrom(this.proyectosService.uploadProcessorImage(file));
          if (response) {
            const imageUrl = response.url;
            console.log('Imagen procesada subida al storage:', imageUrl);
            
            // Guardar en la base de datos
            await firstValueFrom(this.proyectosService.saveImagen(userId, imageUrl));
            console.log('Imagen procesada guardada en BD:', file.name);
            uploadedCount++;
          }
        } catch (error) {
          console.error('Error subiendo imagen procesada:', error);
          // No mostrar error al usuario, solo loguear
        }
      }
    }
    
    // Emitir evento para actualizar la lista de imágenes en el sidebar solo si se subió al menos una imagen
    if (uploadedCount > 0) {
      console.log(`Se subieron ${uploadedCount} imagen(es) procesada(s) al storage y BD`);
      window.dispatchEvent(new CustomEvent('imagesUpdated'));
    }
  }
  
  // Generar miniaturas (previews) para las imágenes cargadas
  generateImagePreviews(files: File[]) {
    // Limpiar URLs anteriores
    this.cleanupImagePreviews();
    
    // Generar nuevas URLs para cada imagen
    this.imagePreviewUrls = [];
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.imagePreviewUrls.push(e.target.result);
          // Si es la primera imagen, generar la vista previa procesada
          if (this.imagePreviewUrls.length === 1 && files.length === 1) {
            this.generateProcessedPreview(e.target.result);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  }
  
  // Generar vista previa procesada con las configuraciones actuales
  generateProcessedPreview(imageDataUrl: string) {
    if (!imageDataUrl || this.selectedFiles.length === 0) return;
    
    this.isGeneratingPreview = true;
    const img = new Image();
    
    img.onload = () => {
      try {
        // Crear canvas para la vista previa
        const canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          this.isGeneratingPreview = false;
          return;
        }
        
        // Establecer color de fondo
        if (this.backgroundColor === 'transparent') {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        } else {
          ctx.fillStyle = this.backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        
        // Calcular dimensiones y posición según el modo
        let drawWidth, drawHeight, drawX, drawY;
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;
        
        if (this.mode === 'adaptar') {
          // Mantener proporción y ajustar dentro del canvas
          if (imgAspect > canvasAspect) {
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgAspect;
          } else {
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgAspect;
          }
          drawX = (canvas.width - drawWidth) / 2;
          drawY = (canvas.height - drawHeight) / 2;
        } else if (this.mode === 'recortar') {
          // Llenar todo el espacio recortando
          if (imgAspect > canvasAspect) {
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgAspect;
            drawX = (canvas.width - drawWidth) / 2;
            drawY = 0;
          } else {
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgAspect;
            drawX = 0;
            drawY = (canvas.height - drawHeight) / 2;
          }
        } else if (this.mode === 'rellenar') {
          // Estirar para llenar todo el espacio
          drawWidth = canvas.width;
          drawHeight = canvas.height;
          drawX = 0;
          drawY = 0;
        } else {
          // Por defecto, adaptar
          if (imgAspect > canvasAspect) {
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgAspect;
          } else {
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgAspect;
          }
          drawX = (canvas.width - drawWidth) / 2;
          drawY = (canvas.height - drawHeight) / 2;
        }
        
        // Aplicar estilos de imagen si es necesario
        // Por ahora, solo dibujamos la imagen directamente
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        
        // Convertir canvas a data URL
        this.processedPreviewUrl = canvas.toDataURL('image/jpeg', 0.92);
        this.isGeneratingPreview = false;
      } catch (error) {
        console.error('Error generando vista previa:', error);
        this.isGeneratingPreview = false;
      }
    };
    
    img.onerror = () => {
      this.isGeneratingPreview = false;
    };
    
    img.src = imageDataUrl;
  }
  
  // Método para actualizar la vista previa cuando cambien las configuraciones
  updatePreview() {
    if (this.imagePreviewUrls.length > 0 && this.selectedFiles.length > 0) {
      this.generateProcessedPreview(this.imagePreviewUrls[0]);
    }
  }
  
  // Limpiar URLs de miniaturas para liberar memoria
  cleanupImagePreviews() {
    this.imagePreviewUrls.forEach(url => {
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        // Las URLs de datos no necesitan ser revocadas, pero las blob sí
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      }
    });
    this.imagePreviewUrls = [];
    this.processedPreviewUrl = null;
  }
  
  onDragOver(event: DragEvent) {
    event.preventDefault();
  }
  
  processImages() {
    if (!this.selectedFile && this.selectedFiles.length === 0) {
      this.showError('Por favor, selecciona un archivo ZIP o imágenes individuales');
      return;
    }

    // Validar autenticación para poder guardar las imágenes procesadas
    this.isAuthenticated = this.authService.isAuthenticated();
    if (!this.isAuthenticated) {
      // Permitir procesar pero mostrar advertencia de que no se guardarán
      console.log('Usuario no autenticado: las imágenes se procesarán pero no se guardarán en BD');
    }

    // Validar autenticación si hay un archivo ZIP (requerido para ZIP)
    if (this.selectedFile && this.selectedFile.name.toLowerCase().endsWith('.zip')) {
      if (!this.isAuthenticated) {
        this.showError('Debes iniciar sesión para poder procesar archivos ZIP. Por favor, inicia sesión para continuar.');
        return;
      }
    }

    // Validar valores
    if (this.width <= 0 || this.height <= 0) {
      this.showError('El ancho y alto deben ser mayores a 0');
      return;
    }

    // Resetear mensajes
    this.errorMessage = '';
    this.successMessage = '';
    this.isProcessing = true;
    this.processingProgress = 0;

    // Preparar nombre original para el archivo de salida
    let originalFileName: string;
    if (this.selectedFile) {
      // Si es un ZIP, usar su nombre sin la extensión
      originalFileName = this.selectedFile.name.replace(/\.zip$/i, '');
    } else if (this.selectedFiles.length > 0) {
      // Si son imágenes individuales, usar el nombre de la primera sin extensión
      const firstFile = this.selectedFiles[0];
      originalFileName = firstFile.name.replace(/\.(jpg|jpeg|png|webp)$/i, '');
    } else {
      originalFileName = 'imagenes';
    }
    
    // Preparar request
    const request = {
      width: this.width,
      height: this.height,
      backgroundColor: this.backgroundColor,
      mode: this.mode,
      imageStyle: this.imageStyle,
      zipFile: this.selectedFile || undefined,
      imageFiles: this.selectedFiles.length > 0 ? this.selectedFiles : undefined,
      originalFileName: originalFileName
    };

    // Simular progreso mientras se procesa
    this.progressInterval = setInterval(() => {
      if (this.processingProgress < 90) {
        this.processingProgress += 10;
      }
    }, 500);

    // Procesar imágenes
    this.imageProcessorService.processImages(request).subscribe({
      next: async (blob: Blob) => {
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        this.processingProgress = 100;

        // Generar nombre de archivo con formato: <nombre original>_fechahorasegundos.zip
        let originalName: string;
        if (this.selectedFile) {
          // Si es un ZIP, usar su nombre sin la extensión
          originalName = this.selectedFile.name.replace(/\.zip$/i, '');
        } else if (this.selectedFiles.length > 0) {
          // Si son imágenes individuales, usar el nombre de la primera sin extensión
          const firstFile = this.selectedFiles[0];
          originalName = firstFile.name.replace(/\.(jpg|jpeg|png|webp)$/i, '');
        } else {
          originalName = 'imagenes';
        }
        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const downloadFileName = `${originalName}_${timestamp}.zip`;
        
        // Crear URL del blob y descargar
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Extraer imágenes del ZIP procesado y subir al storage (tanto para ZIP como imágenes individuales)
        try {
          const zip = new JSZip();
          const zipData = await zip.loadAsync(blob);
          const processedImageFiles: File[] = [];
          
          // Extraer todas las imágenes del ZIP procesado
          for (const fileName in zipData.files) {
            const file = zipData.files[fileName];
            if (!file.dir && /\.(jpg|jpeg|png|webp)$/i.test(fileName)) {
              const imageBlob = await file.async('blob');
              // Determinar el tipo MIME correcto basado en la extensión
              let mimeType = 'image/jpeg';
              if (/\.png$/i.test(fileName)) {
                mimeType = 'image/png';
              } else if (/\.webp$/i.test(fileName)) {
                mimeType = 'image/webp';
              }
              const imageFile = new File([imageBlob], fileName, { type: mimeType });
              processedImageFiles.push(imageFile);
            }
          }
          
          // Subir imágenes procesadas al storage y guardar en BD
          if (processedImageFiles.length > 0) {
            await this.uploadProcessedImages(processedImageFiles);
          }
        } catch (error) {
          console.error('Error extrayendo imágenes del ZIP procesado:', error);
          // Continuar aunque falle la extracción
        }

        this.isProcessing = false;
        this.showSuccess('¡Imágenes procesadas exitosamente! El archivo se está descargando.');
        
        // Resetear los archivos seleccionados después de la descarga
        setTimeout(() => {
          this.selectedFile = null;
          this.selectedFiles = [];
          this.cleanupImagePreviews();
          // Resetear el input de archivo
          const fileInput = document.getElementById('fileInput') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
        }, 1000);
        
        // Limpiar mensaje después de 5 segundos
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
      },
      error: (error) => {
        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        this.isProcessing = false;
        this.processingProgress = 0;
        
        // Detectar errores de conexión
        let errorMessage = 'Error al procesar las imágenes.';
        
        if (error.status === 0 || error.error?.code === 'ECONNREFUSED' || error.message?.includes('ERR_CONNECTION_REFUSED')) {
          errorMessage = 'No se pudo conectar con el servidor. Por favor, asegúrate de que el backend esté corriendo en http://localhost:7071. Ejecuta "npm start" en la carpeta server.';
        } else if (error.status === 404) {
          errorMessage = 'El endpoint del servidor no fue encontrado. Verifica la configuración del backend.';
        } else if (error.status === 500) {
          errorMessage = 'Error interno del servidor al procesar las imágenes.';
        } else if (error.status === 400) {
          errorMessage = 'Datos inválidos. Verifica los parámetros ingresados.';
        }
        
        // Intentar leer el mensaje de error del servidor si es un Blob
        if (error.error instanceof Blob) {
          error.error.text().then((text: string) => {
            try {
              const errorObj = JSON.parse(text);
              if (errorObj.error) {
                errorMessage = errorObj.error;
              }
            } catch {
              // Si no se puede parsear, usar el mensaje por defecto
            }
            this.showError(errorMessage);
          }).catch(() => {
            this.showError(errorMessage);
          });
        } else if (error.error && typeof error.error === 'object' && error.error.error) {
          // Si el error viene como objeto JSON
          errorMessage = error.error.error;
          this.showError(errorMessage);
        } else {
          this.showError(errorMessage);
        }
      }
    });
  }

  private showError(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }

  private showSuccess(message: string) {
    this.successMessage = message;
  }
  
  getCurrentTime() {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  }
  
  toggleTemplateDropdown() {
    this.showTemplateDropdown = !this.showTemplateDropdown;
  }

  selectTemplate(templateId: string | null) {
    this.selectedTemplate = templateId;
    this.showTemplateDropdown = false;
    this.onTemplateSelected(templateId);
  }

  onTemplateSelected(templateId: string | null) {
    if (!templateId) {
      this.selectedTemplate = null;
      return;
    }
    
    const template = this.templates.find(t => t.id === templateId);
    if (template) {
      this.selectedTemplate = templateId;
      this.width = template.width;
      this.height = template.height;
      // Actualizar vista previa después de cambiar la plantilla
      this.updatePreview();
    }
  }

  getSelectedTemplateName(): string {
    if (!this.selectedTemplate) return 'Personalizado';
    const template = this.templates.find(t => t.id === this.selectedTemplate);
    return template ? template.name : 'Personalizado';
  }

  getTemplateIconClass(templateId: string): string {
    const template = this.templates.find(t => t.id === templateId);
    return template?.ratioClass || '';
  }
  
  getSelectedTemplate() {
    if (!this.selectedTemplate) return null;
    return this.templates.find(t => t.id === this.selectedTemplate);
  }
  
  getTemplatePreviewImage(): string | null {
    if (!this.selectedTemplate) return null;
    const template = this.getSelectedTemplate();
    if (!template || !template.image) return null;
    return `assets/${template.image}`;
  }
  
  onColorPickerChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (this.backgroundColor !== 'transparent') {
      this.backgroundColor = target.value;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-template-select')) {
      this.showTemplateDropdown = false;
    }
  }
}
